import { Box, Container } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { MeetingCancelledPanel } from "../../components/Meeting";
import { Locale } from "../../core/api/model";
import { useCurrentUser } from "../../core/hooks/user";
import SpinnerModal from "../../core/spinner-modal";
import { useUpdateMeetingMutation } from "../../generated/platform";
import { useMeeting, useViewAs, ViewAs } from "../hooks/meeting";

interface Props {
  locale: Locale;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export const MeetingCancelled = ({ locale, showErrorModal }: Props) => {
  const { uuid } = useParams<{ uuid: string }>();
  const intl = useIntl();
  const showError = useCallback(
    (raw?: any) => {
      showErrorModal(
        intl.formatMessage({
          id: "MeetingCallError",
          defaultMessage: "Termin fehlgeschlagen",
        }),
        intl.formatMessage({
          id: "MeetingCallErrorText",
          defaultMessage: "Leider konnte der Termin nicht gestartet werden.",
        }),
        raw
      );
    },
    [intl, showErrorModal]
  );
  const { user, loading: userLoading } = useCurrentUser(showError);
  const { meeting, loading: meetingLoading } = useMeeting(uuid, showError);
  const [
    updateMeetingMutation,
    { loading: mutationLoading },
  ] = useUpdateMeetingMutation();
  const [loading, setLoading] = useState(true);
  const viewAs = useViewAs(user, meeting);
  const history = useHistory();

  useEffect(() => {
    setLoading(userLoading || meetingLoading || mutationLoading);
  }, [meetingLoading, userLoading, mutationLoading]);

  useEffect(() => {
    if (meeting?.hasEnded) {
      history.push(`/virtual-meet/ended/${meeting.uuid}`);
    }
  }, [meeting, history]);

  useEffect(() => {
    if (!meeting) {
      return;
    }
    if (viewAs in [ViewAs.Expert, ViewAs.ActiveParticipant]) {
      if (!meeting.status || meeting.status !== "omitted") {
        updateMeetingMutation({
          variables: {
            id: meeting.id,
            input: {
              status: "cancelled",
            },
          },
        }).catch(showError);
      }
    }
  }, [viewAs, meeting, showError, updateMeetingMutation]);

  return (
    <Box
      component={"main"}
      className="main-background"
      pb={{ xs: 7.5, md: 10 }}
      pt={0.01}
    >
      <Container maxWidth="lg">
        <SpinnerModal active={loading} />
        {meeting?.expert && meeting?.participant && meeting?.product && (
          <MeetingCancelledPanel
            viewAs={viewAs}
            locale={locale}
            corporation={{
              //@ts-ignore
              corpName: meeting.corporation.corpName || {},
            }}
            expert={{
              fullName: `${meeting.expert.partName} ${meeting.expert.partSurname}`,
              title: `${meeting.expert.partTitle}`,
              position: `${meeting.expert.partProfPosition}`,
            }}
            participant={{
              fullName: `${meeting.participant.partName} ${meeting.participant.partSurname}`,
              title: `${meeting.participant.partTitle}`,
              position: `${meeting.participant.partProfPosition}`,
              //@ts-ignore
              corpName: meeting.participant.corporation[0]?.corpName || {},
            }}
            date={{
              startDate: meeting.start,
              endDate: meeting.end,
            }}
            //@ts-ignore
            productName={meeting.product.classificationTitle || {}}
            //@ts-ignore
            productDescription={meeting.product.classificationDesc || {}}
          />
        )}
      </Container>
    </Box>
  );
};
